import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  UserCheck, 
  CheckCircle2, 
  Clock, 
  Download,
  Star,
  Shield,
  Trophy,
  ArrowRight,
  Trash2,
  X,
  Mail,
  RotateCcw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = '/AIC-Logo.png';

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDialog, setReportDialog] = useState({ open: false, type: null });
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/stats`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          setStats(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAuthHeaders]);

  const handleExport = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/${type}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm(
      '⚠️ ARE YOU SURE?\n\nThis will permanently delete ALL submissions and achievements for EVERY child, and reset all points and streaks to zero.\n\nThis action CANNOT be undone.'
    )) return;

    if (!window.confirm('Are you really sure? This affects ALL children in the system.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/children/reset-all`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset');
      }
    } catch (error) {
      toast.error('Failed to reset all children');
    }
  };

  const openReport = async (type) => {
    setReportDialog({ open: true, type });
    setReportLoading(true);
    setReportData([]);

    try {
      let endpoint = '';
      switch (type) {
        case 'parents':
          endpoint = '/api/admin/users';
          break;
        case 'children':
          endpoint = '/api/children/all/list';
          break;
        case 'tasks':
          endpoint = '/api/tasks/all';
          break;
        case 'approvals':
          endpoint = '/api/submissions/pending';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their data?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        setReportData(reportData.filter(u => u.user_id !== userId));
        // Refresh stats
        const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (statsRes.ok) setStats(await statsRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Are you sure you want to delete this child and all their data?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/children/${childId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Child deleted successfully');
        setReportData(reportData.filter(c => c.child_id !== childId));
        // Refresh stats
        const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (statsRes.ok) setStats(await statsRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete child');
      }
    } catch (error) {
      toast.error('Failed to delete child');
    }
  };

  const viewChildTasks = (childId) => {
    navigate(`/admin/child/${childId}/tasks`);
  };

  const getReportTitle = () => {
    switch (reportDialog.type) {
      case 'parents': return 'All Parents';
      case 'children': return 'All Children';
      case 'tasks': return 'Active Tasks';
      case 'approvals': return 'Pending Approvals';
      default: return 'Report';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Admin Dashboard</h1>
            <p className="text-[#6B7280] mt-1">Manage the AIC Super Muslims Challenge</p>
          </div>
        </div>

        {/* Logo & Branding */}
        <Card className="bg-islamic-green text-white rounded-3xl border-0 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <img 
                src={LOGO_URL} 
                alt="Alnoor Islamic Center" 
                className="h-20 w-20 object-contain bg-white/10 rounded-2xl p-3"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h2 className="text-3xl font-bold font-outfit">AIC Super Muslims Challenge</h2>
                <p className="text-white/70 text-lg">Alnoor Islamic Center</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Card 
            className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover cursor-pointer"
            onClick={() => openReport('parents')}
            data-testid="stat-parents"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#08513F]/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-[#08513F]" />
              </div>
              <p className="text-3xl font-bold text-[#264653] font-outfit">{stats?.total_users || 0}</p>
              <p className="text-sm text-[#6B7280]">Total Parents</p>
              <p className="text-xs text-[#08513F] mt-2 flex items-center justify-center gap-1">
                View Report <ArrowRight className="w-3 h-3" />
              </p>
            </CardContent>
          </Card>

          <Card 
            className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover cursor-pointer"
            onClick={() => openReport('children')}
            data-testid="stat-children"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#E76F51]/10 flex items-center justify-center mx-auto mb-4">
                <UserCheck className="w-7 h-7 text-[#E76F51]" />
              </div>
              <p className="text-3xl font-bold text-[#264653] font-outfit">{stats?.total_children || 0}</p>
              <p className="text-sm text-[#6B7280]">Total Children</p>
              <p className="text-xs text-[#E76F51] mt-2 flex items-center justify-center gap-1">
                View Report <ArrowRight className="w-3 h-3" />
              </p>
            </CardContent>
          </Card>

          <Card 
            className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover cursor-pointer"
            onClick={() => openReport('tasks')}
            data-testid="stat-tasks"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <p className="text-3xl font-bold text-[#264653] font-outfit">{stats?.active_tasks || 0}</p>
              <p className="text-sm text-[#6B7280]">Active Tasks</p>
              <p className="text-xs text-[#D4AF37] mt-2 flex items-center justify-center gap-1">
                View Report <ArrowRight className="w-3 h-3" />
              </p>
            </CardContent>
          </Card>

          <Card 
            className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover cursor-pointer"
            onClick={() => navigate('/admin/approvals')}
            data-testid="stat-approvals"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-[#264653] font-outfit">{stats?.pending_approvals || 0}</p>
              <p className="text-sm text-[#6B7280]">Pending Approvals</p>
              <p className="text-xs text-yellow-600 mt-2 flex items-center justify-center gap-1">
                Review Now <ArrowRight className="w-3 h-3" />
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Total Points */}
        <Card className="bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-white rounded-3xl border-0 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-lg">Total Points Awarded</p>
                <p className="text-5xl font-bold font-outfit">{stats?.total_points_awarded || 0}</p>
              </div>
              <Star className="w-20 h-20 text-white/30" />
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#264653] font-outfit flex items-center gap-2">
              <Download className="w-5 h-5" />
              Data Export (CSV)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                onClick={() => handleExport('users')}
                variant="outline"
                className="h-auto py-4 rounded-xl border-2 border-[#08513F]/20 hover:border-[#08513F] hover:bg-[#08513F]/5 flex-col"
                data-testid="export-users-btn"
              >
                <Users className="w-6 h-6 mb-2 text-[#08513F]" />
                <span className="font-medium">Users</span>
              </Button>

              <Button
                onClick={() => handleExport('children')}
                variant="outline"
                className="h-auto py-4 rounded-xl border-2 border-[#E76F51]/20 hover:border-[#E76F51] hover:bg-[#E76F51]/5 flex-col"
                data-testid="export-children-btn"
              >
                <UserCheck className="w-6 h-6 mb-2 text-[#E76F51]" />
                <span className="font-medium">Children</span>
              </Button>

              <Button
                onClick={() => handleExport('completions')}
                variant="outline"
                className="h-auto py-4 rounded-xl border-2 border-[#D4AF37]/20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 flex-col"
                data-testid="export-completions-btn"
              >
                <CheckCircle2 className="w-6 h-6 mb-2 text-[#D4AF37]" />
                <span className="font-medium">Completions</span>
              </Button>

              <Button
                onClick={() => handleExport('scores')}
                variant="outline"
                className="h-auto py-4 rounded-xl border-2 border-[#2A9D8F]/20 hover:border-[#2A9D8F] hover:bg-[#2A9D8F]/5 flex-col"
                data-testid="export-scores-btn"
              >
                <Trophy className="w-6 h-6 mb-2 text-[#2A9D8F]" />
                <span className="font-medium">Scores</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reset All Progress */}
        <Card className="bg-white rounded-3xl border border-red-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-600 font-outfit flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Reset All Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <p className="text-sm text-[#6B7280] mb-4">
              This will permanently delete all submissions and achievements for every child, 
              resetting all points and streaks to zero. This action cannot be undone.
            </p>
            <Button
              onClick={handleResetAll}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 rounded-xl"
              data-testid="reset-all-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset All Children
            </Button>
          </CardContent>
        </Card>

        {/* Report Dialog */}
        <Dialog open={reportDialog.open} onOpenChange={(open) => setReportDialog({ open, type: null })}>
          <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                {getReportTitle()}
              </DialogTitle>
            </DialogHeader>
            
            {reportLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                {reportData.length === 0 ? (
                  <p className="text-center text-[#6B7280] py-8">No data found</p>
                ) : (
                  reportData.map((item, index) => (
                    <Card key={index} className="border border-[#D4AF37]/20">
                      <CardContent className="p-4">
                        {reportDialog.type === 'parents' && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={item.picture} />
                                <AvatarFallback className="bg-[#08513F] text-white">
                                  {item.full_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-[#264653]">{item.full_name}</p>
                                  {item.role === 'admin' && (
                                    <Badge className="bg-[#D4AF37] text-white">Admin</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-[#6B7280] flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {item.email}
                                </p>
                              </div>
                            </div>
                            {item.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteUser(item.user_id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}

                        {reportDialog.type === 'children' && (
                          <div className="flex items-center justify-between">
                            <div 
                              className="flex items-center gap-3 flex-1 cursor-pointer"
                              onClick={() => viewChildTasks(item.child_id)}
                            >
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={item.profile_image} />
                                <AvatarFallback className={`${
                                  item.gender === 'Muslim' ? 'bg-[#08513F]' : 'bg-[#E76F51]'
                                } text-white`}>
                                  {item.first_name?.charAt(0)}{item.last_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-bold text-[#264653]">
                                  {item.first_name} {item.last_name}
                                </p>
                                <p className="text-sm text-[#6B7280]">
                                  {item.gender} • Age {item.age} • {item.total_points} pts
                                </p>
                                <p className="text-xs text-[#6B7280]">Parent: {item.parent_email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewChildTasks(item.child_id)}
                                className="text-[#08513F]"
                              >
                                View Tasks
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteChild(item.child_id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}

                        {reportDialog.type === 'tasks' && (
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-[#264653]">{item.name}</p>
                                <Badge className={item.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                                  {item.is_enabled ? 'Active' : 'Disabled'}
                                </Badge>
                              </div>
                              <p className="text-sm text-[#6B7280]">{item.description}</p>
                              <p className="text-xs text-[#D4AF37] mt-1">
                                {item.points} pts • {item.frequency}
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
